import React, { useState, useMemo, useRef, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { db, getCollectionName } from './firebase';
import { collection, query, orderBy, getDocs, resumo } from './firestore-compat';
import { useBusinessHoursPolling, formatLastUpdated } from './hooks/useBusinessHoursPolling';

const nf = v => v === 'Homesenior' ? 'Vitalcare' : (v || '');

// ─────────────────────────────────────────────────────────────────────────────
// Constantes
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_COLOR = {
  'Recebido':                        '#6366f1',
  'Aguardando Avaliação':            '#84cc16',
  'Em Avaliação':                    '#0ea5e9',
  'Avaliação Concluída':             '#10b981',
  'Sessão Clínica':                  '#10b981',
  'Relatório':                       '#f59e0b',
  'Orçamento':                       '#f97316',
  'Aguardando Aprovação da Direção': '#8b5cf6',
  'Envio para Operadora':            '#ec4899',
  'Aguardando Retorno da Operadora': '#64748b',
  'Abordagem à Família':             '#f43f5e',
  'Em Implantação':                  '#14b8a6',
  'Implantado':                      '#22c55e',
  'Cancelado':                       '#ef4444',
};

const TIMING_STAGES = [
  {
    key: 's1',
    label: 'Solicitação → Envio Operadora',
    short: 'Solic → Envio',
    from: null,
    to: 'Envio para Operadora',
  },
  {
    key: 's2',
    label: 'Envio Op. → Abordagem Família',
    short: 'Envio → Abordagem',
    from: 'Envio para Operadora',
    to: 'Abordagem à Família',
    unit: 'hours',
  },
  {
    key: 's3',
    label: 'Abordagem Família → Em Implantação',
    short: 'Abordagem → Impl.',
    from: 'Abordagem à Família',
    to: 'Em Implantação',
  },
  {
    key: 's4',
    label: 'Em Implantação → Implantado',
    short: 'Em Impl. → Implantado',
    from: 'Em Implantação',
    to: 'Implantado',
  },
];

const P = {
  bg: '#f8fafc', white: '#fff', text: '#1e293b', muted: '#64748b',
  border: '#e2e8f0', primary: '#6366f1', green: '#22c55e',
  red: '#ef4444', amber: '#f59e0b', teal: '#14b8a6', rose: '#f43f5e',
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function getTs(historico, status) {
  const entry = (historico || []).find(h => h.status === status);
  if (!entry?.timestamp) return null;
  const d = new Date(entry.timestamp);
  return isNaN(d) ? null : d;
}

function getDataSolic(p) {
  if (p.dataSolicitacao) {
    const s = p.dataSolicitacao;
    const d = new Date(s.length === 10 ? s + 'T00:00' : s);
    if (!isNaN(d)) return d;
  }
  if (p.dataAbertura?.toDate) return p.dataAbertura.toDate();
  return null;
}

function daysBetween(from, to) {
  if (!from || !to) return null;
  const diff = Math.round((to - from) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : null;
}

function hoursBetween(from, to) {
  if (!from || !to) return null;
  const diff = Math.round((to - from) / (1000 * 60 * 60));
  return diff >= 0 ? diff : null;
}

function calcTimings(p) {
  const solic = getDataSolic(p);
  const result = {};
  TIMING_STAGES.forEach(stage => {
    const from = stage.from ? getTs(p.historico, stage.from) : solic;
    const to = getTs(p.historico, stage.to);
    result[stage.key] = stage.unit === 'hours' ? hoursBetween(from, to) : daysBetween(from, to);
  });
  return result;
}

// Cor/etiqueta de velocidade por etapa — limiares diferentes para etapas medidas em horas
function stageColor(stage, avg) {
  if (avg === null) return P.muted;
  return stage.unit === 'hours'
    ? (avg <= 24 ? P.green : avg <= 72 ? P.amber : P.red)
    : (avg <= 7  ? P.green : avg <= 14 ? P.amber : P.red);
}

function stageBadge(stage, avg) {
  if (avg === null) return null;
  return stage.unit === 'hours'
    ? (avg <= 24 ? 'Rápido' : avg <= 72 ? 'Médio' : 'Lento')
    : (avg <= 7  ? 'Rápido' : avg <= 14 ? 'Médio' : 'Lento');
}

function stageUnitWord(stage, v) {
  if (stage.unit === 'hours') return v === 1 ? 'hora' : 'horas';
  return v === 1 ? 'dia' : 'dias';
}

function heatColor(value, max) {
  if (value === null || max === 0) return '#f1f5f9';
  const r = Math.min(1, value / max);
  const interp = (a, b, t) => Math.round(a + (b - a) * t);
  const green = [34, 197, 94];
  const yellow = [251, 191, 36];
  const red = [239, 68, 68];
  const [from, to, t] = r < 0.5 ? [green, yellow, r * 2] : [yellow, red, (r - 0.5) * 2];
  return `rgb(${interp(from[0], to[0], t)},${interp(from[1], to[1], t)},${interp(from[2], to[2], t)})`;
}

function fmtDays(v) {
  if (v === null || v === undefined) return '—';
  return v === 1 ? '1 dia' : `${v} dias`;
}

// Mapa de calor hora do dia × dia da semana para um status específico do histórico
function buildPicoHoras(list, status) {
  const DAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const horaCount = Array(24).fill(0);
  const grid = Array(7).fill(null).map(() => Array(24).fill(0));
  list.forEach(p => {
    const entry = (p.historico || []).find(h => h.status === status);
    if (!entry?.timestamp) return;
    const d = new Date(entry.timestamp);
    if (isNaN(d)) return;
    horaCount[d.getHours()]++;
    grid[d.getDay()][d.getHours()]++;
  });
  const maxGrid = Math.max(...grid.flat(), 1);
  const hasData = horaCount.some(v => v > 0);
  return { horaCount, grid, days: DAYS, maxGrid, hasData };
}

function fmtDateBR(s) {
  if (!s) return '—';
  try {
    const d = new Date(s.length === 10 ? s + 'T00:00' : s);
    return d.toLocaleDateString('pt-BR');
  } catch { return s; }
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────────

function KpiCard({ label, value, sub, color, onClick, active }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? color + '12' : P.white,
        border: `1.5px solid ${active ? color : P.border}`,
        borderRadius: 14,
        padding: '16px 20px',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color .15s, background .15s',
        flex: '1 1 130px',
        minWidth: 110,
      }}
    >
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: .7, marginBottom: 7 }}>{label}</div>
      <div style={{ fontSize: '2rem', fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.71rem', color: P.muted, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e293b', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#f1f5f9', lineHeight: 1.6 }}>
      {label && <div style={{ color: '#94a3b8', fontWeight: 700, marginBottom: 4 }}>{label}</div>}
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color || '#60a5fa' }}>
          {p.name}: <strong>{typeof p.value === 'number' ? p.value.toLocaleString('pt-BR') : p.value}</strong>
        </div>
      ))}
    </div>
  );
}

function CardSection({ title, children, style = {} }) {
  return (
    <div style={{ background: P.white, borderRadius: 14, border: `1px solid ${P.border}`, padding: '20px', marginBottom: 24, ...style }}>
      {title && <h3 style={{ margin: '0 0 16px', fontSize: '0.9rem', fontWeight: 700, color: P.text }}>{title}</h3>}
      {children}
    </div>
  );
}

// Mapa de calor hora × dia da semana — usado tanto para "Solicitação da Operadora" quanto "Envio para Operadora"
function PicoHorasCard({ title, legendaLabel, data }) {
  if (!data.hasData) return null;
  return (
    <CardSection title={title}>
      <p style={{ margin: '-8px 0 14px', fontSize: '0.76rem', color: P.muted }}>
        Hora do dia × dia da semana · Cor mais escura = mais {legendaLabel}.
      </p>

      {/* Grid hora × dia da semana */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.72rem' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '4px 8px', color: P.muted, fontWeight: 600, fontSize: '0.68rem', minWidth: 36 }}>H</th>
              {data.days.map(d => (
                <th key={d} style={{ textAlign: 'center', padding: '4px 6px', color: P.muted, fontWeight: 600, fontSize: '0.68rem', minWidth: 34 }}>{d}</th>
              ))}
              <th style={{ textAlign: 'center', padding: '4px 6px', color: P.muted, fontWeight: 600, fontSize: '0.68rem', minWidth: 40 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 24 }, (_, h) => (
              <tr key={h}>
                <td style={{ padding: '3px 8px', fontWeight: 700, color: P.text, whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
                  {String(h).padStart(2, '0')}h
                </td>
                {data.grid.map((dayArr, di) => {
                  const v = dayArr[h];
                  const intensity = v / data.maxGrid;
                  const bg = v === 0
                    ? '#f8fafc'
                    : intensity < 0.25 ? '#bfdbfe'
                    : intensity < 0.5  ? '#60a5fa'
                    : intensity < 0.75 ? '#2563eb'
                    : '#1e3a8a';
                  const textC = intensity > 0.5 ? '#fff' : P.text;
                  return (
                    <td key={di} style={{ padding: '3px 4px', textAlign: 'center' }}>
                      <div style={{ background: bg, borderRadius: 4, padding: '3px 2px', color: textC, fontWeight: v > 0 ? 700 : 400, fontSize: '0.68rem', minWidth: 26 }}>
                        {v > 0 ? v : '·'}
                      </div>
                    </td>
                  );
                })}
                <td style={{ padding: '3px 6px', textAlign: 'center', fontWeight: 700, color: data.horaCount[h] > 0 ? P.green : P.muted, fontSize: '0.72rem' }}>
                  {data.horaCount[h] > 0 ? data.horaCount[h] : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legenda de cores */}
      <div style={{ marginTop: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.68rem', color: P.muted, marginRight: 4 }}>Volume:</span>
        {[
          { c: '#f8fafc', l: '0' },
          { c: '#bfdbfe', l: '1–25%' },
          { c: '#60a5fa', l: '26–50%' },
          { c: '#2563eb', l: '51–75%' },
          { c: '#1e3a8a', l: '76–100%' },
        ].map(({ c, l }) => (
          <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', color: P.muted }}>
            <span style={{ width: 14, height: 14, borderRadius: 3, background: c, display: 'inline-block', border: '1px solid #e2e8f0' }} />
            {l}
          </span>
        ))}
      </div>
    </CardSection>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main
// ─────────────────────────────────────────────────────────────────────────────

export default function PainelImplantados() {
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aba, setAba] = useState('dashboard'); // 'dashboard' | 'indicadores'

  const [filtroInicio, setFiltroInicio] = useState('');
  const [filtroFim, setFiltroFim]       = useState('');
  const [filtroOp, setFiltroOp]         = useState('');
  const [filtroFilial, setFiltroFilial] = useState('');

  const [drill, setDrill]       = useState(null);
  const [tablePage, setTablePage] = useState(1);
  const tableRef = useRef(null);
  const POR_PAG = 20;

  const carregarPacientes = useCallback(async () => {
    const q = query(
      collection(db, getCollectionName('captacao_pacientes')),
      orderBy('dataAbertura', 'desc'),
      resumo('captacao_resumo') // sem as cópias do formulário de áreas — só datas/status do histórico são usados
    );
    const snap = await getDocs(q);
    setPacientes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    setLoading(false);
  }, []);
  const pacientesPolling = useBusinessHoursPolling(carregarPacientes);

  const operadoras = useMemo(() =>
    [...new Set(pacientes.filter(p => !p.excluido).map(p => p.convenio).filter(Boolean))].sort()
  , [pacientes]);

  const filiais = useMemo(() =>
    [...new Set(pacientes.filter(p => !p.excluido).map(p => nf(p.filial)).filter(Boolean))].sort()
  , [pacientes]);

  // Base: não excluído + filtros de período / operadora / filial
  const base = useMemo(() => pacientes.filter(p => {
    if (p.excluido) return false;
    const dt = getDataSolic(p);
    if (filtroInicio && dt && dt < new Date(filtroInicio + 'T00:00')) return false;
    if (filtroFim   && dt && dt > new Date(filtroFim   + 'T23:59:59')) return false;
    if (filtroOp    && p.convenio !== filtroOp) return false;
    if (filtroFilial && nf(p.filial) !== filtroFilial) return false;
    return true;
  }), [pacientes, filtroInicio, filtroFim, filtroOp, filtroFilial]);

  const cancelados = useMemo(() => base.filter(p => p.canceladoPelaOperadora), [base]);

  // KPIs
  const kpis = useMemo(() => {
    const total        = base.length;
    const implantados  = base.filter(p => p.status === 'Implantado').length;
    const emImpl       = base.filter(p => p.status === 'Em Implantação').length;
    const abordagem    = base.filter(p => p.status === 'Abordagem à Família').length;
    const agRetorno    = base.filter(p => p.status === 'Aguardando Retorno da Operadora').length;
    const cancel       = cancelados.length;
    const conv         = total > 0 ? ((implantados / total) * 100).toFixed(1) : '0.0';
    return { total, implantados, emImpl, abordagem, agRetorno, cancel, conv };
  }, [base, cancelados]);

  // Entradas por operadora (base)
  const entradasPorOp = useMemo(() => {
    const map = {};
    base.forEach(p => {
      const op = p.convenio || 'Não informado';
      if (!map[op]) map[op] = { total: 0, implantados: 0 };
      map[op].total++;
      if (p.status === 'Implantado') map[op].implantados++;
    });
    return Object.entries(map)
      .map(([name, d]) => ({ name, ...d }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 12);
  }, [base]);

  // Taxa de conversão por operadora
  const conversaoPorOp = useMemo(() =>
    entradasPorOp
      .filter(x => x.total >= 1)
      .map(x => ({
        name: x.name,
        taxa: Math.round((x.implantados / x.total) * 100),
        implantados: x.implantados,
        total: x.total,
      }))
      .sort((a, b) => b.taxa - a.taxa)
  , [entradasPorOp]);

  // Timings por paciente
  const baseComTiming = useMemo(() =>
    base.map(p => ({ ...p, _t: calcTimings(p) }))
  , [base]);

  // Estatísticas de tempo por etapa (min / avg / mediana / max)
  const stageTimings = useMemo(() => {
    const result = {};
    TIMING_STAGES.forEach(s => {
      const vals = baseComTiming
        .map(p => p._t[s.key])
        .filter(v => v !== null && v >= 0)
        .sort((a, b) => a - b);
      if (!vals.length) { result[s.key] = { min: null, avg: null, median: null, max: null, n: 0 }; return; }
      const sum = vals.reduce((a, b) => a + b, 0);
      const mid = Math.floor(vals.length / 2);
      result[s.key] = {
        min:    vals[0],
        max:    vals[vals.length - 1],
        avg:    Math.round(sum / vals.length),
        median: vals.length % 2 === 0 ? Math.round((vals[mid - 1] + vals[mid]) / 2) : vals[mid],
        n:      vals.length,
      };
    });
    return result;
  }, [baseComTiming]);

  // Mapa de calor: hora do dia × dia da semana — Solicitação (1º estágio do kanban: 'Recebido')
  const picosSolicitacao = useMemo(() => buildPicoHoras(base, 'Recebido'), [base]);

  // Mapa de calor: hora do dia × dia da semana para envios à operadora
  const picosEnvio = useMemo(() => buildPicoHoras(base, 'Envio para Operadora'), [base]);

  // Heatmap: operadora × etapa → média de dias
  const heatmapData = useMemo(() => {
    const ops = [...new Set(base.map(p => p.convenio).filter(Boolean))].sort();
    const data = {};
    ops.forEach(op => {
      data[op] = {};
      TIMING_STAGES.forEach(s => {
        const vals = baseComTiming
          .filter(p => p.convenio === op && p._t[s.key] !== null)
          .map(p => p._t[s.key]);
        data[op][s.key] = vals.length > 0 ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : null;
      });
    });
    return { ops, data };
  }, [base, baseComTiming]);

  const heatmapMax = useMemo(() => {
    const m = {};
    TIMING_STAGES.forEach(s => {
      const vals = Object.values(heatmapData.data).map(d => d[s.key]).filter(v => v !== null);
      m[s.key] = vals.length > 0 ? Math.max(...vals) : 1;
    });
    return m;
  }, [heatmapData]);

  // Motivos de cancelamento
  const motivosCancelamento = useMemo(() => {
    const map = {};
    cancelados.forEach(p => {
      const m = p.justificativaCancelamento?.trim() || 'Não informado';
      map[m] = (map[m] || 0) + 1;
    });
    return Object.entries(map)
      .map(([motivo, total]) => ({ motivo, total }))
      .sort((a, b) => b.total - a.total);
  }, [cancelados]);

  // Drill-down
  const drillData = useMemo(() => drill ? base.filter(drill.fn) : [], [base, drill]);
  const totalPag  = Math.max(1, Math.ceil(drillData.length / POR_PAG));
  const rowsPag   = drillData.slice((tablePage - 1) * POR_PAG, tablePage * POR_PAG);

  const openDrill = (label, fn) => {
    setDrill(prev => prev?.label === label ? null : { label, fn });
    setTablePage(1);
    setTimeout(() => tableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 160);
  };

  const limparFiltros = () => {
    setFiltroInicio(''); setFiltroFim(''); setFiltroOp(''); setFiltroFilial('');
    setDrill(null); setTablePage(1);
  };

  // ── Indicadores de captação ───────────────────────────────────────────────

  // Período dos indicadores: mês corrente (padrão)
  const [indMesRef, setIndMesRef] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  const indBase = useMemo(() => pacientes.filter(p => {
    if (p.excluido) return false;
    const dt = getDataSolic(p);
    if (!dt) return false;
    const mk = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
    return mk === indMesRef;
  }), [pacientes, indMesRef]);

  const [indAberto, setIndAberto] = useState(null);

  const indicadores = useMemo(() => {
    const total = indBase.length;

    const taxa = (num, den) => {
      if (!den) return { pct: '—', label: 'Sem dados' };
      const p = ((num / den) * 100).toFixed(1);
      return { pct: `${p}%`, label: `${p}% = ${num} paciente${num !== 1 ? 's' : ''}` };
    };

    // ── Ind 1: avaliação iniciada em ≤24h a partir da solicitação ─────────────
    // Denominador: TODAS as captações do mês (129).
    // Quem não tem "Em Avaliação" no histórico é contado como fora do SLA.
    const aval24h = indBase.filter(p => {
      const d = daysBetween(getDataSolic(p), getTs(p.historico, 'Em Avaliação'));
      return d !== null && d <= 1;
    });
    const avalForaP = indBase.filter(p => {
      const d = daysBetween(getDataSolic(p), getTs(p.historico, 'Em Avaliação'));
      return d === null || d > 1;
    });
    // também usado nos inds 4 e como contexto
    const avaliacoes = indBase.filter(p => getTs(p.historico, 'Em Avaliação'));

    // ── Ind 2 & 3: implantação dentro do SLA APÓS AUTORIZAÇÃO da operadora ───
    // "Autorização" = paciente chegou ao status "Abordagem à Família"
    // (operadora aprovou — campo dirAprovado=true no histórico).
    // Medir da solicitação seria impossível (há 10+ etapas antes).
    const autorizadosVM  = indBase.filter(p => p.vm  && getTs(p.historico, 'Abordagem à Família'));
    const autorizadosSVM = indBase.filter(p => !p.vm && getTs(p.historico, 'Abordagem à Família'));

    // VM: ≤48h de "Abordagem à Família" até "Implantado"
    const impl48hVM = autorizadosVM.filter(p => {
      const d = daysBetween(getTs(p.historico, 'Abordagem à Família'), getTs(p.historico, 'Implantado'));
      return d !== null && d <= 2;
    });
    // Sem VM: ≤24h de "Abordagem à Família" até "Implantado"
    const impl24hSVM = autorizadosSVM.filter(p => {
      const d = daysBetween(getTs(p.historico, 'Abordagem à Família'), getTs(p.historico, 'Implantado'));
      return d !== null && d <= 1;
    });

    // ── Ind 4: conversão real avaliação → implantação efetivada ──────────────
    // Numerador: só quem chegou a "Implantado" de fato.
    // "Aguardando Retorno da Operadora" NÃO conta — ainda é pré-implantação.
    const implantadosDeFato = indBase.filter(p => getTs(p.historico, 'Implantado'));
    const naoConvertidos = avaliacoes.filter(p => !getTs(p.historico, 'Implantado'));

    // ── Ind 5: não hospitalização em ≤10 dias após implantação ───────────────
    // Requer que o status "Hospitalizado" seja registrado no histórico de captação.
    // Se nenhum paciente tem esse registro, o indicador não tem dados reais.
    const implDoMes = indBase.filter(p => getTs(p.historico, 'Implantado'));
    const temDadosHosp = implDoMes.some(p => getTs(p.historico, 'Hospitalizado'));
    const naoHosp10d = implDoMes.filter(p => {
      const implDt = getTs(p.historico, 'Implantado');
      if (!implDt) return false;
      const hospDt = getTs(p.historico, 'Hospitalizado');
      return !hospDt || daysBetween(implDt, hospDt) > 10;
    });
    const hosp10d = implDoMes.filter(p => {
      const implDt = getTs(p.historico, 'Implantado');
      if (!implDt) return false;
      const hospDt = getTs(p.historico, 'Hospitalizado');
      return hospDt && daysBetween(implDt, hospDt) <= 10;
    });

    const t1 = taxa(aval24h.length, total);               // den = total do mês
    const t2 = taxa(impl48hVM.length,  autorizadosVM.length);
    const t3 = taxa(impl24hSVM.length, autorizadosSVM.length);
    const t4 = taxa(implantadosDeFato.length, avaliacoes.length);
    const t5 = temDadosHosp
      ? taxa(naoHosp10d.length, implDoMes.length)
      : { pct: '—', label: 'Sem dados' };

    return {
      total,
      i1: {
        label: 'Avaliação iniciada em ≤24h após a solicitação',
        descricao: 'Mede o tempo entre a data/hora da solicitação e o início da avaliação clínica ("Em Avaliação"). Meta: 100% das captações avaliadas em até 24h.',
        ...t1, num: aval24h.length, den: total,
        sub1: `Numerador: ${aval24h.length} captações com avaliação iniciada em ≤24h da solicitação`,
        sub2: `Denominador: ${total} captações totais no mês (inclusive em andamento)`,
        pacs: aval24h,
        pacsForaDen: avalForaP,
        colExtra: { label: 'Dias até avaliação', fn: p => {
          const d = daysBetween(getDataSolic(p), getTs(p.historico, 'Em Avaliação'));
          return d !== null ? `${d} dia(s)` : 'Sem avaliação';
        }},
      },
      i2: {
        label: 'Implantação em ≤48h após autorização — pacientes COM VM',
        descricao: 'Mede o tempo de "Abordagem à Família" (operadora autorizou) até "Implantado". Para VM o SLA é 48h. Denominador: apenas pacientes VM que tiveram autorização registrada.',
        ...t2, num: impl48hVM.length, den: autorizadosVM.length,
        sub1: `Numerador: ${impl48hVM.length} pacientes VM implantados em ≤48h da autorização`,
        sub2: `Denominador: ${autorizadosVM.length} pacientes VM com autorização ("Abordagem à Família") no período`,
        pacs: impl48hVM,
        pacsForaDen: autorizadosVM.filter(p => !impl48hVM.includes(p)),
        colExtra: { label: 'Dias Abordagem→Implant.', fn: p => {
          const d = daysBetween(getTs(p.historico, 'Abordagem à Família'), getTs(p.historico, 'Implantado'));
          return d !== null ? `${d} dia(s)` : '—';
        }},
      },
      i3: {
        label: 'Implantação em ≤24h após autorização — pacientes SEM VM',
        descricao: 'Mede o tempo de "Abordagem à Família" (operadora autorizou) até "Implantado". Para não-VM o SLA é 24h. Denominador: apenas pacientes sem VM com autorização registrada.',
        ...t3, num: impl24hSVM.length, den: autorizadosSVM.length,
        sub1: `Numerador: ${impl24hSVM.length} pacientes sem VM implantados em ≤24h da autorização`,
        sub2: `Denominador: ${autorizadosSVM.length} pacientes sem VM com autorização ("Abordagem à Família") no período`,
        pacs: impl24hSVM,
        pacsForaDen: autorizadosSVM.filter(p => !impl24hSVM.includes(p)),
        colExtra: { label: 'Dias Abordagem→Implant.', fn: p => {
          const d = daysBetween(getTs(p.historico, 'Abordagem à Família'), getTs(p.historico, 'Implantado'));
          return d !== null ? `${d} dia(s)` : '—';
        }},
      },
      i4: {
        label: 'Taxa de conversão: avaliações → implantação efetivada',
        descricao: 'De todos os pacientes que tiveram avaliação clínica iniciada, quantos chegaram de fato ao status "Implantado". Não conta quem está apenas aguardando retorno da operadora.',
        ...t4, num: implantadosDeFato.length, den: avaliacoes.length,
        sub1: `Numerador: ${implantadosDeFato.length} pacientes que chegaram ao status "Implantado"`,
        sub2: `Denominador: ${avaliacoes.length} pacientes com avaliação clínica iniciada no período`,
        pacs: implantadosDeFato,
        pacsForaDen: naoConvertidos,
        colExtra: { label: 'Status atual', fn: p => p.status || '—' },
      },
      i5: {
        label: 'Taxa de não hospitalização nos primeiros 10 dias pós-implantação',
        descricao: 'Pacientes implantados que não precisaram ser hospitalizados nos primeiros 10 dias. Requer registro do status "Hospitalizado" no histórico do paciente para funcionar.',
        ...t5, num: naoHosp10d.length, den: temDadosHosp ? implDoMes.length : 0,
        sub1: temDadosHosp
          ? `Numerador: ${naoHosp10d.length} implantados sem hospitalização em ≤10 dias`
          : 'Sem dados — nenhum registro de "Hospitalizado" encontrado no histórico dos pacientes',
        sub2: temDadosHosp
          ? `Denominador: ${implDoMes.length} pacientes implantados no mês`
          : 'Para ativar este indicador, registre o status "Hospitalizado" ao avançar casos de reinternação',
        pacs: naoHosp10d,
        pacsForaDen: hosp10d,
        colExtra: { label: 'Situação', fn: p => p.status || '—' },
      },
    };
  }, [indBase]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: 12 }}>📊</div>
        <div style={{ color: P.primary, fontWeight: 700, fontSize: '1rem' }}>Carregando dashboard...</div>
      </div>
    </div>
  );

  const chartH = (n) => Math.max(200, n * 38);

  return (
    <div style={{ background: P.bg, minHeight: '100vh', padding: '28px 28px 60px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>

        {/* ── Header ── */}
        <div style={{ marginBottom: 20, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: P.text }}>📊 Dashboard de Captação</h1>
            <p style={{ margin: '4px 0 0', color: P.muted, fontSize: '0.83rem' }}>
              Análise de captação, conversão e tempos do processo de implantação
            </p>
          </div>
          <button onClick={pacientesPolling.refresh} disabled={pacientesPolling.refreshing}
            title="Atualiza sozinho de hora em hora em horário comercial — clique para forçar agora"
            style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', fontWeight: 700, color: P.muted, background: P.white, border: `1px solid ${P.border || '#e2e8f0'}`, borderRadius: 8, padding: '7px 12px', cursor: 'pointer' }}>
            {pacientesPolling.refreshing ? '⏳' : '🔄'} Atualizar {pacientesPolling.lastUpdated ? `· ${formatLastUpdated(pacientesPolling.lastUpdated)}` : ''}
          </button>
        </div>

        {/* ── Abas ── */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f1f5f9', borderRadius: 10, padding: 4, alignSelf: 'flex-start', width: 'fit-content' }}>
          {[
            { key: 'dashboard',    label: '📊 Análise Geral' },
            { key: 'indicadores',  label: '🎯 Indicadores de Captação' },
          ].map(t => (
            <button key={t.key} onClick={() => setAba(t.key)}
              style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700,
                fontSize: '0.83rem', transition: 'all 0.15s',
                background: aba === t.key ? P.white : 'transparent',
                color: aba === t.key ? P.text : P.muted,
                boxShadow: aba === t.key ? '0 1px 4px rgba(0,0,0,0.08)' : 'none' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── ABA: Indicadores de Captação ── */}
        {aba === 'indicadores' && (
          <div>
            {/* Seletor de mês */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <label style={{ fontSize: '0.78rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: .5 }}>Mês de referência</label>
              <input type="month" value={indMesRef} onChange={e => setIndMesRef(e.target.value)}
                style={{ border: `1px solid ${P.border}`, borderRadius: 8, padding: '7px 12px', fontSize: '0.85rem', background: P.white, color: P.text }} />
              <span style={{ fontSize: '0.8rem', color: P.muted }}>{indicadores ? `${indicadores.total} captações no período` : 'Sem dados'}</span>
            </div>

            {!indicadores || !indicadores.total ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: P.muted }}>Nenhuma captação encontrada no mês selecionado.</div>
            ) : (() => {
              const indList = [
                { num: 1, ind: indicadores.i1, icon: '⚡', color: '#0ea5e9', listLabel: 'Avaliados em ≤24h', listLabelFora: 'Fora do prazo (>24h)' },
                { num: 2, ind: indicadores.i2, icon: '🔴', color: '#ef4444', listLabel: 'Implantados em ≤48h (VM)', listLabelFora: 'Fora do prazo ou não implantados (VM)' },
                { num: 3, ind: indicadores.i3, icon: '🟢', color: '#22c55e', listLabel: 'Implantados em ≤24h (sem VM)', listLabelFora: 'Fora do prazo ou não implantados (sem VM)' },
                { num: 4, ind: indicadores.i4, icon: '🔄', color: '#8b5cf6', listLabel: 'Convertidos em implantação', listLabelFora: 'Não convertidos' },
                { num: 5, ind: indicadores.i5, icon: '🏠', color: '#f59e0b', listLabel: 'Não hospitalizados em ≤10 dias', listLabelFora: 'Hospitalizados em ≤10 dias' },
              ];

              const thSt = { padding: '8px 12px', textAlign: 'left', borderBottom: `1px solid ${P.border}`,
                fontSize: '0.68rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: .5, color: P.muted, background: '#f8fafc' };
              const tdSt = { padding: '8px 12px', fontSize: '0.8rem', color: P.text, borderBottom: `1px solid #f1f5f9` };

              const PacTable = ({ pacs, colExtra, label: tLabel, color }) => (
                <div style={{ marginTop: 0 }}>
                  <div style={{ padding: '8px 12px', background: `${color}12`, borderBottom: `1px solid ${color}30`,
                    fontSize: '0.75rem', fontWeight: 700, color }}>
                    {tLabel} ({pacs.length})
                  </div>
                  {pacs.length === 0 ? (
                    <div style={{ padding: '16px 12px', color: P.muted, fontSize: '0.8rem', textAlign: 'center' }}>Sem dados</div>
                  ) : (
                    <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            {['Nº IW', 'Nome', 'Convênio', 'Filial', 'Solicitação', colExtra?.label].filter(Boolean).map(h => (
                              <th key={h} style={thSt}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {pacs.map((p, i) => (
                            <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                              <td style={tdSt}>{p.numero || '—'}</td>
                              <td style={{ ...tdSt, fontWeight: 600 }}>{p.nome || '—'}</td>
                              <td style={tdSt}>{p.convenio || '—'}</td>
                              <td style={tdSt}>{nf(p.filial) || '—'}</td>
                              <td style={tdSt}>{fmtDateBR(p.dataSolicitacao)}</td>
                              {colExtra && <td style={{ ...tdSt, fontWeight: 600, color }}>{colExtra.fn(p)}</td>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {indList.map(({ num, ind, icon, color, listLabel, listLabelFora }) => {
                    const aberto = indAberto === num;
                    const semDados = !ind.den;
                    return (
                      <div key={num} style={{ background: P.white, border: `1px solid ${P.border}`, borderRadius: 14,
                        overflow: 'hidden', borderLeft: `5px solid ${color}` }}>
                        {/* Cabeçalho do card */}
                        <div style={{ padding: '18px 22px', display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                          <div style={{ fontSize: '1.8rem', lineHeight: 1, flexShrink: 0 }}>{icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: .6, color: P.muted, marginBottom: 3 }}>Indicador {num}</div>
                            <div style={{ fontWeight: 700, fontSize: '0.92rem', color: P.text, marginBottom: 4, lineHeight: 1.4 }}>{ind.label}</div>
                            {ind.descricao && (
                              <div style={{ fontSize: '0.75rem', color: P.muted, marginBottom: 10, lineHeight: 1.5, fontStyle: 'italic' }}>
                                ℹ️ {ind.descricao}
                              </div>
                            )}

                            {/* Valor principal */}
                            {semDados ? (
                              <div>
                                <div style={{ fontSize: '1.5rem', fontWeight: 800, color: P.muted, marginBottom: 6 }}>Sem dados</div>
                                {ind.sub2 && <div style={{ fontSize: '0.73rem', color: '#f59e0b', fontWeight: 600 }}>⚠️ {ind.sub2}</div>}
                              </div>
                            ) : (
                              <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
                                <span style={{ fontSize: '2.6rem', fontWeight: 900, color, lineHeight: 1 }}>{ind.pct}</span>
                                <span style={{ fontSize: '1rem', fontWeight: 700, color: P.text }}>= {ind.num} paciente{ind.num !== 1 ? 's' : ''}</span>
                                <span style={{ fontSize: '0.82rem', color: P.muted }}>de {ind.den} elegíveis</span>
                              </div>
                            )}

                            {/* Numerador / Denominador */}
                            {!semDados && (
                              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
                                <span style={{ background: `${color}12`, color, border: `1px solid ${color}30`,
                                  borderRadius: 20, padding: '3px 12px', fontSize: '0.73rem', fontWeight: 700 }}>
                                  ↑ {ind.sub1}
                                </span>
                                <span style={{ background: '#f1f5f9', color: P.muted, borderRadius: 20,
                                  padding: '3px 12px', fontSize: '0.73rem', fontWeight: 600 }}>
                                  ÷ {ind.sub2}
                                </span>
                              </div>
                            )}
                          </div>

                          {/* Botão listagem */}
                          {!semDados && (
                            <button onClick={() => setIndAberto(aberto ? null : num)}
                              style={{ flexShrink: 0, padding: '7px 14px', borderRadius: 8, border: `1px solid ${aberto ? color : P.border}`,
                                background: aberto ? `${color}12` : P.white, color: aberto ? color : P.muted,
                                fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                              {aberto ? '▲ Fechar' : '▼ Ver listagem'}
                            </button>
                          )}
                        </div>

                        {/* Listagem expandível */}
                        {aberto && !semDados && (
                          <div style={{ borderTop: `1px solid ${P.border}` }}>
                            <PacTable pacs={ind.pacs} colExtra={ind.colExtra} label={listLabel} color={color} />
                            {ind.pacsForaDen && ind.pacsForaDen.length > 0 && (
                              <PacTable pacs={ind.pacsForaDen} colExtra={ind.colExtra} label={listLabelFora} color={P.red} />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Ind 6 e 7 — Manual */}
                  {[
                    { num: 6, icon: '💰', color: '#14b8a6', label: 'Efetividade na margem do custo operacional de implantação até 30 dias',
                      nota: 'Registro manual — configure no módulo financeiro' },
                    { num: 7, icon: '⭐', color: '#f59e0b', label: 'Taxa de satisfação dos novos clientes até 10 dias após a implantação',
                      nota: 'Registro manual — configure no módulo de qualidade' },
                  ].map(({ num, icon, color, label, nota }) => (
                    <div key={num} style={{ background: '#fafafa', border: `1px dashed ${P.border}`, borderRadius: 14,
                      padding: '18px 22px', borderLeft: `5px solid ${color}`, display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                      <div style={{ fontSize: '1.8rem', lineHeight: 1 }}>{icon}</div>
                      <div>
                        <div style={{ fontSize: '0.67rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: .6, color: P.muted, marginBottom: 3 }}>Indicador {num}</div>
                        <div style={{ fontWeight: 700, fontSize: '0.92rem', color: P.text, marginBottom: 8, lineHeight: 1.4 }}>{label}</div>
                        <div style={{ fontSize: '1.6rem', fontWeight: 800, color: P.muted, marginBottom: 6 }}>Sem dados</div>
                        <div style={{ fontSize: '0.73rem', color: '#f59e0b', fontWeight: 600 }}>⚠️ {nota}</div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── ABA: Dashboard Geral — conteúdo original ── */}
        {aba === 'dashboard' && <>

        {/* ── Filtros ── */}
        <CardSection style={{ marginBottom: 24, padding: '16px 20px' }}>
          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {[
              { label: 'Solicitação de', value: filtroInicio, set: setFiltroInicio },
              { label: 'até',            value: filtroFim,    set: setFiltroFim },
            ].map(({ label, value, set }) => (
              <div key={label}>
                <label style={{ display: 'block', fontSize: '0.71rem', fontWeight: 700, color: P.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>{label}</label>
                <input type="date" value={value} onChange={e => set(e.target.value)}
                  style={{ border: `1px solid ${P.border}`, borderRadius: 8, padding: '7px 10px', fontSize: '0.83rem', background: P.white, color: P.text }} />
              </div>
            ))}
            <div>
              <label style={{ display: 'block', fontSize: '0.71rem', fontWeight: 700, color: P.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>Operadora</label>
              <select value={filtroOp} onChange={e => setFiltroOp(e.target.value)}
                style={{ border: `1px solid ${P.border}`, borderRadius: 8, padding: '7px 10px', fontSize: '0.83rem', background: P.white, color: P.text, minWidth: 160 }}>
                <option value="">Todas</option>
                {operadoras.map(op => <option key={op} value={op}>{op}</option>)}
              </select>
            </div>
            {filiais.length > 1 && (
              <div>
                <label style={{ display: 'block', fontSize: '0.71rem', fontWeight: 700, color: P.muted, marginBottom: 4, textTransform: 'uppercase', letterSpacing: .5 }}>Unidade</label>
                <select value={filtroFilial} onChange={e => setFiltroFilial(e.target.value)}
                  style={{ border: `1px solid ${P.border}`, borderRadius: 8, padding: '7px 10px', fontSize: '0.83rem', background: P.white, color: P.text }}>
                  <option value="">Todas</option>
                  {filiais.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            )}
            {(filtroInicio || filtroFim || filtroOp || filtroFilial) && (
              <button onClick={limparFiltros}
                style={{ padding: '7px 14px', borderRadius: 8, border: `1px solid ${P.border}`, background: P.white, color: P.muted, cursor: 'pointer', fontSize: '0.82rem' }}>
                ✕ Limpar filtros
              </button>
            )}
          </div>
        </CardSection>

        {/* ── KPIs ── */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 24 }}>
          <KpiCard label="Total de avaliações" value={kpis.total}       color={P.primary}  sub="casos no período"           onClick={() => openDrill('Todos',                    () => true)}                                                         active={drill?.label === 'Todos'} />
          <KpiCard label="Implantados"          value={kpis.implantados} color={P.green}    sub={`${kpis.conv}% de conversão`} onClick={() => openDrill('Implantados',              p => p.status === 'Implantado')}                                     active={drill?.label === 'Implantados'} />
          <KpiCard label="Em implantação"       value={kpis.emImpl}      color={P.teal}     sub="em andamento agora"          onClick={() => openDrill('Em Implantação',           p => p.status === 'Em Implantação')}                                 active={drill?.label === 'Em Implantação'} />
          <KpiCard label="Abordagem família"    value={kpis.abordagem}   color={P.rose}     sub="aguardando confirmação"      onClick={() => openDrill('Abordagem à Família',      p => p.status === 'Abordagem à Família')}                           active={drill?.label === 'Abordagem à Família'} />
          <KpiCard label="Ag. retorno op."      value={kpis.agRetorno}   color="#64748b"    sub="aguardando resposta"         onClick={() => openDrill('Ag. Retorno Operadora',    p => p.status === 'Aguardando Retorno da Operadora')}               active={drill?.label === 'Ag. Retorno Operadora'} />
          <KpiCard label="Cancelados"           value={kpis.cancel}      color={P.red}      sub={kpis.total > 0 ? `${((kpis.cancel / kpis.total) * 100).toFixed(1)}% do total` : '—'} onClick={() => openDrill('Cancelados', p => p.canceladoPelaOperadora)} active={drill?.label === 'Cancelados'} />
        </div>

        {/* ── Gráficos: Entradas + Conversão ── */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>

          <CardSection title="📥 Entradas por operadora" style={{ marginBottom: 0 }}>
            <div style={{ height: chartH(entradasPorOp.length) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={entradasPorOp} layout="vertical" margin={{ top: 0, right: 30, left: 0, bottom: 0 }} barCategoryGap="25%">
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" tick={{ fontSize: 11, fill: P.muted }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 11, fill: P.text }} axisLine={false} tickLine={false} />
                  <RTooltip content={<ChartTip />} cursor={{ fill: 'rgba(99,102,241,0.05)' }} />
                  <Bar dataKey="total"       name="Total"       fill={P.primary} radius={[0, 4, 4, 0]} barSize={12} />
                  <Bar dataKey="implantados" name="Implantados" fill={P.green}   radius={[0, 4, 4, 0]} barSize={12} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ display: 'flex', gap: 16, marginTop: 10, justifyContent: 'center' }}>
              {[{ c: P.primary, l: 'Total' }, { c: P.green, l: 'Implantados' }].map(({ c, l }) => (
                <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.76rem', color: P.muted }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: 'inline-block' }} />{l}
                </span>
              ))}
            </div>
          </CardSection>

          <CardSection title="📈 Taxa de conversão por operadora" style={{ marginBottom: 0 }}>
            <div style={{ height: chartH(conversaoPorOp.length) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={conversaoPorOp} layout="vertical" margin={{ top: 0, right: 50, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
                  <XAxis type="number" domain={[0, 100]} tickFormatter={v => `${v}%`} tick={{ fontSize: 11, fill: P.muted }} axisLine={false} tickLine={false} />
                  <YAxis dataKey="name" type="category" width={130} tick={{ fontSize: 11, fill: P.text }} axisLine={false} tickLine={false} />
                  <RTooltip
                    content={({ active, payload }) => {
                      if (!active || !payload?.length) return null;
                      const d = payload[0]?.payload;
                      return (
                        <div style={{ background: '#1e293b', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#f1f5f9' }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>{d.name}</div>
                          <div>Conversão: <strong>{d.taxa}%</strong></div>
                          <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>{d.implantados} implantados de {d.total} solicitações</div>
                        </div>
                      );
                    }}
                    cursor={{ fill: 'rgba(34,197,94,0.05)' }}
                  />
                  <Bar dataKey="taxa" name="Conversão %" radius={[0, 4, 4, 0]} barSize={12}>
                    {conversaoPorOp.map((e, i) => (
                      <Cell key={i} fill={e.taxa >= 70 ? P.green : e.taxa >= 40 ? P.amber : P.red} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 12, justifyContent: 'center' }}>
              {[{ c: P.green, l: '≥70% — ótimo' }, { c: P.amber, l: '40–69% — médio' }, { c: P.red, l: '<40% — baixo' }].map(({ c, l }) => (
                <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: '0.72rem', color: P.muted }}>
                  <span style={{ width: 10, height: 10, borderRadius: 2, background: c, display: 'inline-block' }} />{l}
                </span>
              ))}
            </div>
          </CardSection>
        </div>

        {/* ── Tempos por etapa ── */}
        <CardSection title="⏱ Tempos por etapa">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            {TIMING_STAGES.map(stage => {
              const t = stageTimings[stage.key];
              const avg = t.avg;
              const color = stageColor(stage, avg);
              const badge = stageBadge(stage, avg);
              return (
                <div key={stage.key} style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', border: `1px solid ${P.border}` }}>
                  <div style={{ fontSize: '0.67rem', fontWeight: 700, color: P.muted, textTransform: 'uppercase', letterSpacing: .5, marginBottom: 8, lineHeight: 1.4 }}>{stage.label}</div>

                  {/* Média — destaque principal */}
                  <div style={{ fontSize: '1.8rem', fontWeight: 800, color, lineHeight: 1 }}>{avg ?? '—'}</div>
                  <div style={{ fontSize: '0.7rem', color: P.muted, marginTop: 2 }}>
                    {avg !== null ? `${avg} ${stageUnitWord(stage, avg)} em média · ${t.n} casos` : 'sem dados suficientes'}
                  </div>

                  {/* Min / Mediana / Máx */}
                  {t.n > 0 && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, marginTop: 10 }}>
                      {[
                        { lbl: 'Mín',   val: t.min,    c: P.green },
                        { lbl: 'Med',   val: t.median, c: P.amber },
                        { lbl: 'Máx',   val: t.max,    c: P.red   },
                      ].map(({ lbl, val, c }) => (
                        <div key={lbl} style={{ background: c + '10', borderRadius: 6, padding: '5px 6px', textAlign: 'center', border: `1px solid ${c}25` }}>
                          <div style={{ fontSize: '0.58rem', fontWeight: 700, color: c, textTransform: 'uppercase', letterSpacing: .4 }}>{lbl}</div>
                          <div style={{ fontSize: '1rem', fontWeight: 800, color: c, lineHeight: 1.2 }}>{val ?? '—'}</div>
                          <div style={{ fontSize: '0.58rem', color: P.muted }}>{val !== null ? stageUnitWord(stage, val) : ''}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {badge && (
                    <span style={{ display: 'inline-block', marginTop: 8, fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 20, background: color + '18', color, border: `1px solid ${color}40` }}>
                      {badge}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </CardSection>

        {/* ── Pico de horários — Solicitação da Operadora (1º estágio do kanban) ── */}
        <PicoHorasCard title="🕐 Pico de horários — Solicitação da Operadora" legendaLabel="solicitações" data={picosSolicitacao} />

        {/* ── Pico de horários de Envio para Operadora ── */}
        <PicoHorasCard title="🕐 Pico de horários — Envio para Operadora" legendaLabel="envios" data={picosEnvio} />

        {/* ── Mapa de calor ── */}
        {heatmapData.ops.length > 0 && (
          <CardSection title="🔥 Mapa de calor — Tempo médio por operadora × etapa">
            <p style={{ margin: '-8px 0 14px', fontSize: '0.76rem', color: P.muted }}>
              Verde = processo rápido · Amarelo = médio · Vermelho = lento. Clique num KPI acima para filtrar.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px 14px', color: P.muted, fontWeight: 600, fontSize: '0.71rem', borderBottom: `1px solid ${P.border}`, minWidth: 150 }}>Operadora</th>
                    {TIMING_STAGES.map(s => (
                      <th key={s.key} style={{ textAlign: 'center', padding: '8px 14px', color: P.muted, fontWeight: 600, fontSize: '0.71rem', borderBottom: `1px solid ${P.border}`, minWidth: 130 }}>
                        {s.short} {s.unit === 'hours' ? '(h)' : '(d)'}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmapData.ops.map(op => (
                    <tr key={op}
                      style={{ cursor: 'pointer' }}
                      onClick={() => openDrill(`Operadora: ${op}`, p => p.convenio === op)}
                    >
                      <td style={{ padding: '8px 14px', fontWeight: 600, color: P.text, borderBottom: `1px solid #f8fafc`, whiteSpace: 'nowrap' }}>{op}</td>
                      {TIMING_STAGES.map(s => {
                        const val = heatmapData.data[op][s.key];
                        const bg  = heatColor(val, heatmapMax[s.key]);
                        const ratio = val !== null ? val / heatmapMax[s.key] : 0;
                        return (
                          <td key={s.key} style={{ padding: '6px 10px', textAlign: 'center', borderBottom: `1px solid #f8fafc` }}>
                            <div style={{ background: bg, borderRadius: 7, padding: '6px 10px', color: ratio > 0.55 ? '#fff' : P.text, fontWeight: 700, fontSize: '0.82rem' }}>
                              {val !== null ? `${val}${s.unit === 'hours' ? 'h' : 'd'}` : '—'}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={{ margin: '10px 0 0', fontSize: '0.72rem', color: P.muted }}>Clique em uma linha para ver os pacientes daquela operadora na tabela abaixo.</p>
          </CardSection>
        )}

        {/* ── Cancelamentos ── */}
        {cancelados.length > 0 && (
          <div style={{ background: P.white, borderRadius: 14, border: `1.5px solid #fecaca`, padding: '20px', marginBottom: 24 }}>
            <h3 style={{ margin: '0 0 14px', fontSize: '0.9rem', fontWeight: 700, color: '#dc2626' }}>
              ❌ Motivos de cancelamento — {cancelados.length} {cancelados.length === 1 ? 'caso' : 'casos'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {motivosCancelamento.map(({ motivo, total }) => {
                const pct = Math.round((total / cancelados.length) * 100);
                const isActive = drill?.label === `Cancelado: ${motivo}`;
                return (
                  <div
                    key={motivo}
                    onClick={() => openDrill(`Cancelado: ${motivo}`, p => p.canceladoPelaOperadora && (p.justificativaCancelamento?.trim() || 'Não informado') === motivo)}
                    style={{ cursor: 'pointer', borderRadius: 9, padding: '10px 14px', border: `1px solid ${isActive ? '#dc2626' : '#fee2e2'}`, background: isActive ? '#fff1f2' : '#fff5f5', transition: 'border-color .1s' }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 5 }}>
                      <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#7f1d1d' }}>{motivo}</span>
                      <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#dc2626', whiteSpace: 'nowrap', marginLeft: 12 }}>{total} · {pct}%</span>
                    </div>
                    <div style={{ background: '#fecaca', borderRadius: 4, height: 5, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#dc2626', borderRadius: 4 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Tabela de drill-down ── */}
        {drill && (
          <div ref={tableRef} style={{ background: P.white, borderRadius: 14, border: `1px solid ${P.border}`, padding: '20px', overflowX: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: P.text }}>📋 {drill.label}</h3>
                <div style={{ fontSize: '0.72rem', color: P.muted, marginTop: 3 }}>
                  {drillData.length} {drillData.length === 1 ? 'registro' : 'registros'} · Pág {tablePage} de {totalPag}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setTablePage(p => p - 1)} disabled={tablePage <= 1}
                  style={{ padding: '5px 12px', borderRadius: 7, border: `1px solid ${P.border}`, background: P.white, cursor: tablePage <= 1 ? 'default' : 'pointer', opacity: tablePage <= 1 ? .4 : 1, fontSize: '0.8rem', color: P.text }}>
                  ← Ant.
                </button>
                <button onClick={() => setTablePage(p => p + 1)} disabled={tablePage >= totalPag}
                  style={{ padding: '5px 12px', borderRadius: 7, border: `1px solid ${P.border}`, background: P.white, cursor: tablePage >= totalPag ? 'default' : 'pointer', opacity: tablePage >= totalPag ? .4 : 1, fontSize: '0.8rem', color: P.text }}>
                  Próx. →
                </button>
                <button onClick={() => setDrill(null)}
                  style={{ padding: '5px 12px', borderRadius: 7, border: `1px solid ${P.border}`, background: P.white, cursor: 'pointer', color: P.muted, fontSize: '0.8rem' }}>
                  ✕ Fechar
                </button>
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Nº', 'Paciente', 'Status', 'Operadora', 'Unidade', 'Solicitação', 'Cancelamento / Observação'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', borderBottom: `1px solid ${P.border}`, color: P.muted, fontWeight: 600, fontSize: '0.71rem', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowsPag.map((p, i) => {
                  const statusKey = p.canceladoPelaOperadora ? 'Cancelado' : p.status;
                  const sc = STATUS_COLOR[statusKey] || '#94a3b8';
                  return (
                    <tr key={p.id || i}
                      onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      style={{ borderBottom: `1px solid #f1f5f9`, transition: 'background .1s' }}
                    >
                      <td style={{ padding: '9px 12px', color: P.muted, fontSize: '0.72rem' }}>{p.numero || '—'}</td>
                      <td style={{ padding: '9px 12px', fontWeight: 600, color: P.text, whiteSpace: 'nowrap' }}>{p.nome || '—'}</td>
                      <td style={{ padding: '9px 12px' }}>
                        <span style={{ background: sc + '20', color: sc, padding: '2px 9px', borderRadius: 20, fontWeight: 600, fontSize: '0.71rem', whiteSpace: 'nowrap', border: `1px solid ${sc}30` }}>
                          {statusKey}
                        </span>
                      </td>
                      <td style={{ padding: '9px 12px', color: P.text, whiteSpace: 'nowrap' }}>{p.convenio || '—'}</td>
                      <td style={{ padding: '9px 12px', color: P.muted, whiteSpace: 'nowrap' }}>{nf(p.filial) || '—'}</td>
                      <td style={{ padding: '9px 12px', color: P.muted, whiteSpace: 'nowrap' }}>{fmtDateBR(p.dataSolicitacao)}</td>
                      <td style={{ padding: '9px 12px', color: p.canceladoPelaOperadora ? '#dc2626' : P.muted, fontSize: '0.76rem', maxWidth: 220 }}>
                        {p.canceladoPelaOperadora
                          ? (p.justificativaCancelamento || 'Não informado')
                          : (p.observacoes || '—')}
                      </td>
                    </tr>
                  );
                })}
                {rowsPag.length === 0 && (
                  <tr><td colSpan={7} style={{ padding: '32px 12px', textAlign: 'center', color: P.muted }}>Nenhum registro encontrado</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        </> }

      </div>
    </div>
  );
}
